import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2, Pencil, Trash2 } from 'lucide-react'
import { toast } from 'sonner'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import { getErrorMessage } from '@/lib/api'
import { getPlannerLinkModelLabel, getPlannerLinkResourceLabel } from '@/lib/planner-links'
import { catalogRolesApi, type CatalogRole } from '@/services/catalog-roles'
import resourcePlannerApi, { type PlannerResourceLinkPayload } from '@/services/resource-planner'
import crewsApi from '@/services/crews'
import { storeCommerceApi } from '@/services/store-commerce'
import { useAuthStore } from '@/stores/auth-store'
import type { PlannerModel, PlannerResourceDefinition, PlannerResourceLink } from '@/types'

type PlannerTargetType = 'product' | 'product_variant' | 'addon_item' | 'order' | 'global_order'

type PlannerLinkManagerProps = {
  title?: string
  description?: string
  resourceLabel?: string
  resourceLabelPlural?: string
  useCatalogRolesForResourcePicker?: boolean
  className?: string
  targetType: PlannerTargetType
  targetId: string | number | null
  serviceInstanceId: string | number | null
  serveTypeId?: string | number | null
  initialLinks?: PlannerResourceLink[]
  onChanged?: () => Promise<void> | void
}

type PlannerLinkFormState = {
  id: string | number | null
  plannerModelId: string
  plannerResourceKind: string
  plannerResourceId: string
  defaultCrewId: string
  jobTriggerStatus: string
  jobCreationMode: string
  quantity: string
  scope: string
  isRequired: boolean
  notes: string
}

const emptyForm: PlannerLinkFormState = {
  id: null,
  plannerModelId: '',
  plannerResourceKind: '',
  plannerResourceId: '',
  defaultCrewId: '',
  jobTriggerStatus: '',
  jobCreationMode: 'auto',
  quantity: '1',
  scope: 'default',
  isRequired: true,
  notes: '',
}

const scopeOptions = [
  { value: 'default', label: 'Default' },
  { value: 'optional', label: 'Optional' },
  { value: 'override', label: 'Override' },
]

const jobCreationModeOptions = [
  { value: 'auto', label: 'Auto' },
  { value: 'manual', label: 'Manual' },
]

const formatResourceKindLabel = (kind: string) => {
  const normalized = kind.trim().toLowerCase()
  if (normalized === 'crew') return 'Team'
  return kind
    .trim()
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

const toSlug = (value: string) => value.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')

const toPkValue = (value: string | number | null | undefined): number | null => {
  if (typeof value === 'number') {
    return Number.isInteger(value) && value >= 0 ? value : null
  }

  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (!trimmed || !/^\d+$/.test(trimmed)) {
      return null
    }

    const parsed = Number(trimmed)
    return Number.isInteger(parsed) && parsed >= 0 ? parsed : null
  }

  return null
}

type BranchServiceInstanceOption = {
  id?: string | number | null
  instanceId?: string | null
  serviceId?: string | null
  code?: string | null
}

const resolveBranchPkFromServiceInstanceReference = (
  reference: string | number | null | undefined,
  branches: BranchServiceInstanceOption[],
): number | null => {
  const directPk = toPkValue(reference)
  if (directPk != null) {
    return directPk
  }

  const normalizedReference = String(reference ?? '').trim()
  if (!normalizedReference) {
    return null
  }

  for (const branch of branches) {
    const branchPk = toPkValue(branch.id)
    const branchInstanceId = String(branch.instanceId ?? branch.serviceId ?? branch.code ?? '').trim()
    const branchIdText = String(branch.id ?? '').trim()

    if (branchIdText && branchIdText === normalizedReference) {
      return branchPk
    }
    if (branchInstanceId && branchInstanceId === normalizedReference) {
      return branchPk
    }
  }

  return null
}

const formatCatalogRoleOptionLabel = (role: CatalogRole): string => {
  const roleName = String(role.name || '').trim()
  if (role.is_system) {
    return `${roleName} (System)`
  }
  if (role.scope_type === 'organization') {
    return `${roleName} (Global)`
  }
  if (role.scope_type === 'service_instance') {
    return `${roleName} (Local)`
  }
  return roleName
}

const catalogRoleMatchesValue = (role: CatalogRole, value: string) => {
  const normalized = String(value || '').trim()
  if (!normalized) {
    return false
  }
  if (String(role.id) === normalized) {
    return true
  }
  return Boolean(role.unique_id && String(role.unique_id) === normalized)
}

const upsertLink = (links: PlannerResourceLink[], nextLink: PlannerResourceLink) => {
  const nextId = String(nextLink.id)
  if (!nextId) {
    return links
  }

  const existingIndex = links.findIndex((link) => String(link.id) === nextId)
  if (existingIndex === -1) {
    return [...links, nextLink]
  }

  const updated = [...links]
  updated[existingIndex] = nextLink
  return updated
}

export function PlannerLinkManager({
  title = 'Planner resources',
  description = 'Attach planner resource roles to this record so planning can reserve the right people, vehicles, or equipment.',
  resourceLabel = 'Planner resource',
  resourceLabelPlural = 'Planner resources',
  useCatalogRolesForResourcePicker = false,
  className,
  targetType,
  targetId,
  serviceInstanceId,
  serveTypeId,
  initialLinks,
  onChanged,
}: PlannerLinkManagerProps) {
  const resourceLabelLower = resourceLabel.toLowerCase()
  const resourceLabelPluralLower = resourceLabelPlural.toLowerCase()
  const availableBranchesForScope = useAuthStore((state) => state.availableBranches)
  const currentBranchForScope = useAuthStore((state) => state.currentBranch)
  const branchesForDropdown = useMemo(() => {
    const list = [...(availableBranchesForScope ?? [])]
    if (currentBranchForScope && !list.some((branch) => String(branch.id) === String(currentBranchForScope.id))) {
      list.push(currentBranchForScope)
    }
    return list
  }, [availableBranchesForScope, currentBranchForScope])
  const queryClient = useQueryClient()
  const [form, setForm] = useState<PlannerLinkFormState>(emptyForm)
  const [selectedDefaultGroupKey, setSelectedDefaultGroupKey] = useState('')
  const serviceInstanceFilterValue = useMemo(() => {
    if (serviceInstanceId == null) {
      return ''
    }
    return String(serviceInstanceId).trim()
  }, [serviceInstanceId])
  const modelListServiceInstanceId = useMemo(() => {
    if (!serviceInstanceFilterValue) {
      return undefined
    }
    return serviceInstanceFilterValue
  }, [serviceInstanceFilterValue])

  const modelsQuery = useQuery({
    queryKey: ['planner-models', modelListServiceInstanceId ?? 'all'],
    queryFn: () => resourcePlannerApi.listModels({
      service_instance_id: modelListServiceInstanceId,
      published: true,
      active: true,
    }),
    enabled: Boolean(modelListServiceInstanceId),
    staleTime: 60_000,
  })

  const models = useMemo(
    () => (modelsQuery.data ?? []).filter((model) => !model.is_system),
    [modelsQuery.data],
  )

  const linksQuery = useQuery({
    queryKey: ['planner-resource-links', targetType, targetId, serviceInstanceFilterValue, serveTypeId ?? null],
    queryFn: () => {
      if (targetType === 'global_order') {
        if (!serviceInstanceFilterValue) {
          return Promise.resolve([])
        }
        return resourcePlannerApi.listResourceLinks({
          service_instance_id: serviceInstanceFilterValue,
          apply_to_all_orders: 1,
          ...(serveTypeId ? { serve_type_id: serveTypeId } : {}),
        })
      }

      return resourcePlannerApi.listResourceLinks({ [`${targetType}_id`]: targetId } as Record<string, string | number>)
    },
    enabled: targetType === 'global_order' ? serviceInstanceFilterValue.length > 0 : Boolean(targetId),
    initialData: initialLinks,
  })

  const links = linksQuery.data ?? []

  const orderStatusesQuery = useQuery({
    queryKey: ['planner-order-statuses', serviceInstanceFilterValue, serveTypeId ?? null],
    queryFn: () => storeCommerceApi.getOrderStatuses({
      service_instance_id: serviceInstanceFilterValue || undefined,
      serve_type_id: serveTypeId ?? undefined,
      order_type: serveTypeId ?? undefined,
    }),
    enabled: targetType === 'global_order',
    staleTime: 60_000,
  })

  const orderDefaultGroups = useMemo(() => {
    if (targetType !== 'global_order') {
      return [] as Array<{
        key: string
        serveTypeLabel: string
        triggerStatusLabel: string
        modeLabel: string
        links: PlannerResourceLink[]
      }>
    }

    const groups = new Map<string, {
      key: string
      serveTypeLabel: string
      triggerStatusLabel: string
      modeLabel: string
      links: PlannerResourceLink[]
    }>()

    links.forEach((link) => {
      const serveType = String(link.serve_type ?? 'all').trim() || 'all'
      const trigger = String(link.job_trigger_status ?? '').trim() || 'unset'
      const mode = String(link.job_creation_mode ?? 'auto').trim().toLowerCase() || 'auto'
      const key = `${serveType}::${trigger}::${mode}`
      const existing = groups.get(key)

      if (existing) {
        existing.links.push(link)
        return
      }

      groups.set(key, {
        key,
        serveTypeLabel: link.serve_type_label || 'All order types',
        triggerStatusLabel: (() => {
          const statusKey = String(link.job_trigger_status ?? '').trim()
          if (!statusKey) {
            return 'Not set'
          }
          return orderStatusesQuery.data?.find((status) => String(status.key) === statusKey)?.label ?? statusKey
        })(),
        modeLabel: (() => {
          const mode = String(link.job_creation_mode ?? 'auto').trim().toLowerCase()
          return jobCreationModeOptions.find((option) => option.value === mode)?.label ?? 'Auto'
        })(),
        links: [link],
      })
    })

    return Array.from(groups.values())
  }, [links, orderStatusesQuery.data, targetType])

  const selectedDefaultGroup = useMemo(() => {
    if (targetType !== 'global_order') {
      return null
    }

    if (!selectedDefaultGroupKey) {
      return orderDefaultGroups[0] ?? null
    }

    return orderDefaultGroups.find((group) => group.key === selectedDefaultGroupKey) ?? (orderDefaultGroups[0] ?? null)
  }, [orderDefaultGroups, selectedDefaultGroupKey, targetType])

  const linksForSelectedDefault = useMemo(() => {
    if (targetType !== 'global_order') {
      return links
    }
    return selectedDefaultGroup?.links ?? []
  }, [links, selectedDefaultGroup, targetType])

  const selectedModel = useMemo<PlannerModel | null>(() => {
    if (!form.plannerModelId) {
      return null
    }
    return models.find((model) => String(model.id) === form.plannerModelId) ?? null
  }, [form.plannerModelId, models])

  useEffect(() => {
    if (!form.plannerModelId || modelsQuery.isLoading || (modelsQuery.isFetching && models.length === 0)) {
      return
    }

    const modelExists = models.some((model) => String(model.id) === String(form.plannerModelId))
    if (!modelExists) {
      setForm((current) => ({
        ...current,
        plannerModelId: '',
        plannerResourceKind: '',
        plannerResourceId: '',
      }))
      toast.error('Selected planner model is not available for this branch. Please choose a valid model.')
    }
  }, [form.plannerModelId, models, modelsQuery.isFetching, modelsQuery.isLoading])

  const effectiveServiceInstanceId = useMemo(() => {
    if (serviceInstanceFilterValue) {
      return serviceInstanceFilterValue
    }
    const ownerServiceInstance = String(selectedModel?.owner_service_instance ?? '').trim()
    return ownerServiceInstance || null
  }, [serviceInstanceFilterValue, selectedModel?.owner_service_instance])

  const selectedModelOwnerServiceInstanceId = useMemo(() => {
    return resolveBranchPkFromServiceInstanceReference(selectedModel?.owner_service_instance, branchesForDropdown)
  }, [branchesForDropdown, selectedModel?.owner_service_instance])

  const plannerCatalogServiceInstanceId = useMemo(() => {
    const ownerScoped = String(selectedModelOwnerServiceInstanceId ?? '').trim()
    if (ownerScoped) {
      if (/[A-Za-z_-]/.test(ownerScoped)) {
        return ownerScoped
      }

      const matchedBranch = branchesForDropdown.find((branch) => String(branch?.id ?? '').trim() === ownerScoped)
      const resolvedInstanceId = String(matchedBranch?.instanceId ?? matchedBranch?.serviceId ?? '').trim()
      if (resolvedInstanceId) {
        return resolvedInstanceId
      }
    }

    return String(effectiveServiceInstanceId ?? serviceInstanceFilterValue ?? '').trim() || null
  }, [branchesForDropdown, effectiveServiceInstanceId, selectedModelOwnerServiceInstanceId, serviceInstanceFilterValue])

  const catalogRolesQuery = useQuery({
    queryKey: ['planner-link-catalog-roles', plannerCatalogServiceInstanceId, selectedModel?.id],
    queryFn: () => catalogRolesApi.list({
      serviceInstanceId: plannerCatalogServiceInstanceId ?? undefined,
      includeHidden: true,
    }),
    enabled: useCatalogRolesForResourcePicker && Boolean(plannerCatalogServiceInstanceId) && Boolean(selectedModel?.id),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  })

  const catalogRoleOptions = useMemo(() => {
    return (catalogRolesQuery.data ?? [])
      .filter((role) => role.active !== false && role.active !== 0)
      .slice()
      .sort((left, right) => formatCatalogRoleOptionLabel(left).localeCompare(formatCatalogRoleOptionLabel(right)))
  }, [catalogRolesQuery.data])

  const resourcesQuery = useQuery({
    queryKey: ['planner-model-resources', selectedModel?.id],
    queryFn: () => resourcePlannerApi.listResources({ planner_model_id: selectedModel?.id ?? undefined }),
    enabled: Boolean(selectedModel?.id),
    staleTime: 60_000,
  })

  const selectedResources = useMemo<PlannerResourceDefinition[]>(() => {
    if (selectedModel?.resource_definitions?.length) {
      return selectedModel.resource_definitions
    }

    return resourcesQuery.data ?? []
  }, [resourcesQuery.data, selectedModel])

  const resourceKinds = useMemo(() => {
    const kinds = Array.from(new Set(
      selectedResources
        .map((resource) => String(resource.resource_kind ?? '').trim())
        .filter(Boolean),
    ))
    return kinds
  }, [selectedResources])

  const selectedKind = useMemo(() => String(form.plannerResourceKind || '').trim().toLowerCase(), [form.plannerResourceKind])
  const isAddonTarget = targetType === 'addon_item'
  const shouldAskKindFirst = isAddonTarget
  const isHumanKind = selectedKind === 'human'
  const isCrewKind = selectedKind === 'crew'
  const needsExplicitResourcePicker = isHumanKind || isCrewKind
  const resourcesForSelectedKind = useMemo(() => {
    if (!shouldAskKindFirst || !selectedKind) {
      return selectedResources
    }
    return selectedResources.filter((resource) => String(resource.resource_kind ?? '').trim().toLowerCase() === selectedKind)
  }, [selectedKind, selectedResources, shouldAskKindFirst])

  const selectedResource = useMemo(
    () => selectedResources.find((resource) => String(resource.id) === form.plannerResourceId) ?? null,
    [form.plannerResourceId, selectedResources],
  )

  const readyCrewsQuery = useQuery({
    queryKey: ['ready-crews', selectedResource?.crew_template, serviceInstanceFilterValue],
    queryFn: () => crewsApi.listReadyCrews({
      crew_template_id: selectedResource?.crew_template as string | number,
      service_instance_id: serviceInstanceFilterValue || undefined,
    }),
    enabled: selectedResource?.resource_kind === 'crew' && Boolean(selectedResource?.crew_template && serviceInstanceFilterValue),
    staleTime: 30_000,
  })

  const hydrateLink = (link: PlannerResourceLink): PlannerResourceLink => {
    const plannerModelId = String(link.planner_model_id ?? link.planner_model ?? '')
    const plannerResourceId = String(link.planner_resource_id ?? link.planner_resource ?? '')
    const model = models.find((entry) => String(entry.id) === plannerModelId) ?? null
    const resource = selectedResources.find((entry) => String(entry.id) === plannerResourceId) ?? null

    return {
      ...link,
      planner_model: link.planner_model ?? model?.id ?? null,
      planner_model_id: link.planner_model_id ?? model?.id ?? null,
      planner_model_code: link.planner_model_code ?? model?.code ?? null,
      planner_model_name: link.planner_model_name ?? model?.name ?? null,
      planner_model_template_code: link.planner_model_template_code ?? model?.template_code ?? null,
      planner_resource: link.planner_resource ?? resource?.id ?? null,
      planner_resource_id: link.planner_resource_id ?? resource?.id ?? null,
      planner_resource_role_code: link.planner_resource_role_code ?? resource?.role_code ?? null,
      planner_resource_label: link.planner_resource_label ?? resource?.label ?? null,
      planner_resource_kind: link.planner_resource_kind ?? resource?.resource_kind ?? null,
    }
  }

  useEffect(() => {
    if (form.id || form.plannerModelId || models.length === 0) {
      return
    }

    const fallbackModelId = links[0]?.planner_model_id ?? links[0]?.planner_model ?? models[0]?.id
    if (fallbackModelId != null) {
      setForm((current) => ({ ...current, plannerModelId: String(fallbackModelId) }))
    }
  }, [form.id, form.plannerModelId, links, models])

  useEffect(() => {
    if (useCatalogRolesForResourcePicker || !selectedModel) {
      return
    }

    const resourceExists = selectedResources.some((resource) => String(resource.id) === form.plannerResourceId)
    if (!resourceExists) {
      setForm((current) => ({
        ...current,
        plannerResourceId: selectedResources[0] ? String(selectedResources[0].id) : '',
      }))
    }
  }, [form.plannerResourceId, selectedModel, selectedResources, useCatalogRolesForResourcePicker])

  useEffect(() => {
    if (!shouldAskKindFirst) {
      return
    }

    if (!form.plannerResourceKind && resourceKinds.length > 0) {
      setForm((current) => ({ ...current, plannerResourceKind: resourceKinds[0] }))
    }
  }, [form.plannerResourceKind, resourceKinds, shouldAskKindFirst])

  useEffect(() => {
    if (!shouldAskKindFirst || !selectedKind) {
      return
    }

    const existsInKind = resourcesForSelectedKind.some((resource) => String(resource.id) === form.plannerResourceId)
    if (!existsInKind) {
      setForm((current) => ({
        ...current,
        plannerResourceId: resourcesForSelectedKind[0] ? String(resourcesForSelectedKind[0].id) : '',
      }))
    }
  }, [form.plannerResourceId, resourcesForSelectedKind, selectedKind, shouldAskKindFirst])

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!targetId) {
        if (targetType !== 'global_order') {
          throw new Error(`Save this record first before linking ${resourceLabelPluralLower}`)
        }
      }
      if (targetType !== 'global_order' && !targetId) {
        throw new Error(`Save this record first before linking ${resourceLabelPluralLower}`)
      }
      if (!serviceInstanceId) {
        throw new Error('No service instance is available for this record')
      }
      if (effectiveServiceInstanceId == null) {
        throw new Error('No valid service instance is available for this record')
      }
      if (!form.plannerModelId) {
        throw new Error('Select a planner model')
      }
      const plannerModelIsValid = models.some((model) => String(model.id) === String(form.plannerModelId))
      if (!plannerModelIsValid) {
        throw new Error('Selected planner model is not available for this branch')
      }
      let resolvedResourceId = shouldAskKindFirst && !needsExplicitResourcePicker
        ? (resourcesForSelectedKind[0] ? String(resourcesForSelectedKind[0].id) : form.plannerResourceId)
        : form.plannerResourceId

      if (useCatalogRolesForResourcePicker) {
        if (!form.plannerResourceId) {
          throw new Error(`Select a ${resourceLabelLower}`)
        }

        const selectedCatalogRole = catalogRoleOptions.find((role) => catalogRoleMatchesValue(role, form.plannerResourceId))
        if (!selectedCatalogRole) {
          throw new Error('Selected catalog role is not available for this service instance.')
        }

        let matchingResource = selectedResources.find((resource) => String(resource.role_name ?? '') === String(selectedCatalogRole.id))
        if (!matchingResource) {
          const roleCode = String(selectedCatalogRole.unique_id ?? '').trim() || toSlug(selectedCatalogRole.name) || `role-${selectedCatalogRole.id}`
          matchingResource = await resourcePlannerApi.createResource({
            planner_model: form.plannerModelId,
            role_name: selectedCatalogRole.id,
            role_code: roleCode,
            label: String(selectedCatalogRole.name || roleCode).trim(),
            resource_kind: 'human',
            is_primary: false,
            min_count: 1,
            max_count: 1,
            ordering: selectedResources.length,
          })
          await queryClient.invalidateQueries({ queryKey: ['planner-model-resources', selectedModel?.id] })
        }

        resolvedResourceId = String(matchingResource.id)
      } else if (!form.plannerResourceId) {
        if (!resolvedResourceId) {
          throw new Error(`Select a ${resourceLabelLower}`)
        }
      }

      const quantity = Number(form.quantity)
      if (!Number.isInteger(quantity) || quantity < 1) {
        throw new Error('Quantity must be a whole number greater than zero')
      }

      const payload: PlannerResourceLinkPayload = {
        service_instance: effectiveServiceInstanceId,
        planner_model: form.plannerModelId,
        planner_resource: resolvedResourceId,
        quantity,
        scope: form.scope,
        is_required: form.isRequired,
        notes: form.notes.trim(),
        config_json: form.defaultCrewId
          ? { default_crew_id: Number(form.defaultCrewId) }
          : {},
      }

      if (targetType === 'global_order') {
        payload.apply_to_all_orders = true
        if (!form.jobTriggerStatus) {
          throw new Error('Select the order status that should trigger jobs planning')
        }
        payload.job_trigger_status = form.jobTriggerStatus
        payload.job_creation_mode = form.jobCreationMode
        if (serveTypeId) {
          payload.serve_type = serveTypeId
        }
      } else {
        payload[targetType] = targetId as string | number
      }

      return resourcePlannerApi.upsertResourceLink(form.id, payload)
    },
    onSuccess: async (savedLink) => {
      queryClient.setQueryData<PlannerResourceLink[]>(['planner-resource-links', targetType, targetId, serviceInstanceFilterValue, serveTypeId ?? null], (current) => {
        const existingLinks = current ?? []
        return upsertLink(existingLinks, hydrateLink(savedLink))
      })
      await queryClient.invalidateQueries({ queryKey: ['planner-resource-links', targetType, targetId, serviceInstanceFilterValue, serveTypeId ?? null] })
      if (onChanged) {
        await onChanged()
      }
      toast.success(form.id ? `${resourceLabel} link updated` : `${resourceLabel} link added`)
      setForm((current) => ({
        ...emptyForm,
        plannerModelId: current.plannerModelId,
      }))
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string | number) => resourcePlannerApi.deleteResourceLinkIfExists(id),
    onSuccess: async (_, deletedId) => {
      queryClient.setQueryData<PlannerResourceLink[]>(['planner-resource-links', targetType, targetId, serviceInstanceFilterValue, serveTypeId ?? null], (current) => {
        return (current ?? []).filter((link) => String(link.id) !== String(deletedId))
      })
      await queryClient.invalidateQueries({ queryKey: ['planner-resource-links', targetType, targetId, serviceInstanceFilterValue, serveTypeId ?? null] })
      if (onChanged) {
        await onChanged()
      }
      toast.success(`${resourceLabel} link removed`)
      setForm((current) => current.id ? { ...emptyForm, plannerModelId: current.plannerModelId } : current)
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const resolveCatalogRoleIdFromLink = (link: PlannerResourceLink) => {
    const roleNameId = link.planner_resource_role_name_id
    if (roleNameId != null && String(roleNameId).trim()) {
      return String(roleNameId)
    }

    const linkedResource = selectedResources.find((resource) => String(resource.id) === String(link.planner_resource_id ?? link.planner_resource ?? ''))
    if (linkedResource?.role_name != null && String(linkedResource.role_name).trim()) {
      return String(linkedResource.role_name)
    }

    return String(link.planner_resource_id ?? link.planner_resource ?? '')
  }

  const startEdit = (link: PlannerResourceLink) => {
    setForm({
      id: link.id,
      plannerModelId: String(link.planner_model_id ?? link.planner_model ?? ''),
      plannerResourceKind: String(link.planner_resource_kind ?? ''),
      plannerResourceId: useCatalogRolesForResourcePicker
        ? resolveCatalogRoleIdFromLink(link)
        : String(link.planner_resource_id ?? link.planner_resource ?? ''),
      jobTriggerStatus: String(link.job_trigger_status ?? ''),
      jobCreationMode: String(link.job_creation_mode ?? 'auto'),
      quantity: String(link.quantity ?? 1),
      scope: link.scope || 'default',
      isRequired: link.is_required !== false,
      notes: link.notes || '',
      defaultCrewId: link.default_crew_id ? String(link.default_crew_id) : String(link.config_json?.default_crew_id ?? ''),
    })
  }

  useEffect(() => {
    if (targetType !== 'global_order') {
      return
    }

    if (orderDefaultGroups.length === 0) {
      setSelectedDefaultGroupKey('')
      return
    }

    if (!selectedDefaultGroupKey || !orderDefaultGroups.some((group) => group.key === selectedDefaultGroupKey)) {
      setSelectedDefaultGroupKey(orderDefaultGroups[0].key)
    }
  }, [orderDefaultGroups, selectedDefaultGroupKey, targetType])

  useEffect(() => {
    if (targetType !== 'global_order' || form.id || !selectedDefaultGroup) {
      return
    }

    const sample = selectedDefaultGroup.links[0]
    if (!sample) {
      return
    }

    setForm((current) => ({
      ...current,
      jobTriggerStatus: String(sample.job_trigger_status ?? current.jobTriggerStatus ?? ''),
      jobCreationMode: String(sample.job_creation_mode ?? current.jobCreationMode ?? 'auto'),
    }))
  }, [form.id, selectedDefaultGroup, targetType])

  return (
    <div className={cn('space-y-4 rounded-2xl border border-border/70 bg-background/70 p-4', className)}>
      <div className="space-y-1">
        <h3 className="text-sm font-semibold uppercase tracking-[0.16em] text-muted-foreground">{title}</h3>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>

      {!targetId && targetType !== 'global_order' ? (
        <div className="rounded-xl border border-dashed border-border/70 bg-muted/20 p-4 text-sm text-muted-foreground">
          Save this record first, then attach {resourceLabelPluralLower}.
        </div>
      ) : null}

      {targetId && !serviceInstanceFilterValue ? (
        <div className="rounded-xl border border-dashed border-amber-300/70 bg-amber-50/70 p-4 text-sm text-amber-900">
          Planner links need a service instance id. Reload this workspace from a branch-scoped service context, then try again.
        </div>
      ) : null}

      <div className={cn('space-y-4', targetType === 'global_order' && 'grid items-start gap-4 lg:grid-cols-[300px_minmax(0,1fr)]')}>
        <div className={cn('space-y-4', targetType === 'global_order' && 'order-2')}>
          <div className="grid gap-3 md:grid-cols-2">
            <div className="space-y-2">
              <Label>Planner model</Label>
              <Select value={form.plannerModelId} onValueChange={(value) => setForm((current) => ({ ...current, plannerModelId: value, plannerResourceId: '' }))}>
                <SelectTrigger>
                  <SelectValue placeholder={modelsQuery.isLoading ? 'Loading planner models...' : 'Select planner model'} />
                </SelectTrigger>
                <SelectContent>
                  {models.map((model) => (
                    <SelectItem key={String(model.id)} value={String(model.id)}>
                      {model.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {shouldAskKindFirst ? (
              <div className="space-y-2">
                <Label>Resource kind</Label>
                <Select value={form.plannerResourceKind} onValueChange={(value) => setForm((current) => ({ ...current, plannerResourceKind: value, plannerResourceId: '' }))}>
                  <SelectTrigger>
                    <SelectValue placeholder={form.plannerModelId ? (resourcesQuery.isLoading ? 'Loading resource kinds...' : 'Select resource kind') : 'Choose a planner model first'} />
                  </SelectTrigger>
                  <SelectContent>
                    {resourceKinds.map((kind) => (
                      <SelectItem key={kind} value={kind}>{formatResourceKindLabel(kind)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : null}
            {!shouldAskKindFirst || needsExplicitResourcePicker ? (
              <div className="space-y-2">
                <Label>{resourceLabel}</Label>
                <Select value={form.plannerResourceId} onValueChange={(value) => setForm((current) => ({ ...current, plannerResourceId: value }))}>
                  <SelectTrigger>
                    <SelectValue placeholder={form.plannerModelId ? (
                      useCatalogRolesForResourcePicker
                        ? (catalogRolesQuery.isLoading ? 'Loading catalog roles...' : `Select ${resourceLabelLower}`)
                        : (resourcesQuery.isLoading ? `Loading ${resourceLabelPluralLower}...` : `Select ${resourceLabelLower}`)
                    ) : 'Choose a planner model first'} />
                  </SelectTrigger>
                  <SelectContent>
                    {useCatalogRolesForResourcePicker
                      ? catalogRoleOptions.map((role) => (
                        <SelectItem key={String(role.id)} value={String(role.id)}>
                          {formatCatalogRoleOptionLabel(role)}
                        </SelectItem>
                      ))
                      : (shouldAskKindFirst ? resourcesForSelectedKind : selectedResources).map((resource) => (
                        <SelectItem key={String(resource.id)} value={String(resource.id)}>
                          {resource.label}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                {useCatalogRolesForResourcePicker ? (
                  <p className="text-xs text-muted-foreground">
                    Lists organization-wide (Global) and branch-local (Local) catalog roles for this planner model&apos;s service instance.
                  </p>
                ) : null}
              </div>
            ) : null}
            <div className="space-y-2">
              <Label>Quantity</Label>
              <Input inputMode="numeric" value={form.quantity} onChange={(event) => setForm((current) => ({ ...current, quantity: event.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>Scope</Label>
              <Select value={form.scope} onValueChange={(value) => setForm((current) => ({ ...current, scope: value }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {scopeOptions.map((scope) => (
                    <SelectItem key={scope.value} value={scope.value}>{scope.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {selectedResource?.resource_kind === 'crew' ? (
              <div className="space-y-2 md:col-span-2">
                <Label>Default team (optional)</Label>
                <Select value={form.defaultCrewId || 'none'} onValueChange={(value) => setForm((current) => ({ ...current, defaultCrewId: value === 'none' ? '' : value }))}>
                  <SelectTrigger>
                    <SelectValue placeholder={readyCrewsQuery.isLoading ? 'Loading ready teams...' : 'Select staffed team'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No default team</SelectItem>
                    {(readyCrewsQuery.data ?? []).map((crew) => (
                      <SelectItem key={String(crew.id)} value={String(crew.id)}>{crew.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">When set, all team members auto-assign to the job at planning time.</p>
              </div>
            ) : null}
            {targetType === 'global_order' ? (
              <div className="space-y-2">
                <Label>Job trigger status</Label>
                <Select value={form.jobTriggerStatus} onValueChange={(value) => setForm((current) => ({ ...current, jobTriggerStatus: value }))}>
                  <SelectTrigger>
                    <SelectValue placeholder={orderStatusesQuery.isLoading ? 'Loading order statuses...' : 'Select order status'} />
                  </SelectTrigger>
                  <SelectContent>
                    {(orderStatusesQuery.data ?? []).map((status) => (
                      <SelectItem key={String(status.key)} value={String(status.key)}>{status.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : null}
            {targetType === 'global_order' ? (
              <div className="space-y-2">
                <Label>Job creation mode</Label>
                <Select value={form.jobCreationMode} onValueChange={(value) => setForm((current) => ({ ...current, jobCreationMode: value }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select trigger mode" />
                  </SelectTrigger>
                  <SelectContent>
                    {jobCreationModeOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : null}
            <div className="space-y-2 md:col-span-2">
              <Label>Notes</Label>
              <Textarea
                value={form.notes}
                onChange={(event) => setForm((current) => ({ ...current, notes: event.target.value }))}
                placeholder="Add planning notes or capability hints"
                rows={3}
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Required</Label>
              <div className="flex items-center justify-between rounded-lg border border-border/70 px-3 py-2">
                <span className="text-sm text-muted-foreground">{form.isRequired ? 'Required for planning' : 'Optional preference only'}</span>
                <Switch checked={form.isRequired} onCheckedChange={(checked) => setForm((current) => ({ ...current, isRequired: checked }))} />
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button type="button" onClick={() => saveMutation.mutate()} disabled={(targetType !== 'global_order' && !targetId) || saveMutation.isPending || modelsQuery.isLoading || (useCatalogRolesForResourcePicker ? catalogRolesQuery.isLoading : resourcesQuery.isLoading) || effectiveServiceInstanceId == null}>
              {saveMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {form.id ? 'Update planner link' : 'Add planner link'}
            </Button>
            {form.id ? (
              <Button type="button" variant="outline" onClick={() => setForm((current) => ({ ...emptyForm, plannerModelId: current.plannerModelId || form.plannerModelId }))}>
                Cancel edit
              </Button>
            ) : null}
          </div>

          {targetType === 'global_order' ? (
            <div className="space-y-2 rounded-xl border border-border/70 bg-muted/10 p-3">
              <p className="text-sm font-semibold text-foreground">{resourceLabelPlural} in selected default</p>
              {!selectedDefaultGroup ? (
                <p className="text-sm text-muted-foreground">Select an order default from the left panel to view its {resourceLabelPluralLower}.</p>
              ) : linksForSelectedDefault.length === 0 ? (
                <p className="text-sm text-muted-foreground">No {resourceLabelPluralLower} are linked to this default yet.</p>
              ) : (
                linksForSelectedDefault.map((link) => (
                  <div key={String(link.id)} className="rounded-lg border border-border/70 bg-background p-3">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="font-medium text-foreground">{getPlannerLinkResourceLabel(link)}</p>
                        <p className="text-sm text-muted-foreground">{getPlannerLinkModelLabel(link)} • Qty {link.quantity ?? 1}</p>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button type="button" variant="ghost" size="icon" onClick={() => startEdit(link)} aria-label="Edit planner link">
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button type="button" variant="ghost" size="icon" className="text-destructive hover:bg-destructive/10 hover:text-destructive" onClick={() => deleteMutation.mutate(link.id)} aria-label="Delete planner link" disabled={deleteMutation.isPending}>
                          {deleteMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                        </Button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          ) : null}
        </div>

        <div className={cn('space-y-2', targetType === 'global_order' ? 'order-1 rounded-xl border border-border/70 bg-muted/10 p-3' : 'order-2')}>
          {targetType === 'global_order' ? (
            <div className="mb-3 flex items-center justify-between gap-2">
              <p className="text-sm font-semibold text-foreground">Order execution setup</p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  setSelectedDefaultGroupKey('')
                  setForm((current) => ({ ...emptyForm, plannerModelId: current.plannerModelId }))
                }}
              >
                New default
              </Button>
            </div>
          ) : (
            <Separator />
          )}

          {targetType === 'global_order' ? (
            linksQuery.isLoading ? (
              <p className="flex items-center text-sm text-muted-foreground"><Loader2 className="mr-2 h-4 w-4 animate-spin" />Loading order execution setup...</p>
            ) : orderDefaultGroups.length === 0 ? (
              <p className="text-sm text-muted-foreground">No order execution setup found. Create the first setup from the form on the right.</p>
            ) : (
              orderDefaultGroups.map((group) => (
                <button
                  key={group.key}
                  type="button"
                  className={cn(
                    'w-full rounded-xl border px-3 py-3 text-left transition',
                    selectedDefaultGroup?.key === group.key
                      ? 'border-amber-300 bg-amber-50/70'
                      : 'border-border/70 bg-background hover:border-amber-200',
                  )}
                  onClick={() => {
                    setSelectedDefaultGroupKey(group.key)
                    const firstLink = group.links[0]
                    setForm((current) => ({
                      ...current,
                      id: null,
                      jobTriggerStatus: String(firstLink?.job_trigger_status ?? current.jobTriggerStatus ?? ''),
                      jobCreationMode: String(firstLink?.job_creation_mode ?? current.jobCreationMode ?? 'auto'),
                    }))
                  }}
                >
                  <p className="font-medium text-foreground">{group.serveTypeLabel}</p>
                  <p className="text-xs text-muted-foreground">Trigger: {group.triggerStatusLabel}</p>
                  <p className="text-xs text-muted-foreground">Mode: {group.modeLabel}</p>
                  <p className="text-xs text-muted-foreground">{resourceLabelPlural}: {group.links.length}</p>
                </button>
              ))
            )
          ) : modelsQuery.isLoading ? (
            <p className="flex items-center text-sm text-muted-foreground"><Loader2 className="mr-2 h-4 w-4 animate-spin" />Loading planner models...</p>
          ) : !serviceInstanceFilterValue ? null : models.length === 0 ? (
            <p className="text-sm text-muted-foreground">No planner models are available for this service instance yet. Create one in Planner Studio first.</p>
          ) : form.plannerModelId && (useCatalogRolesForResourcePicker ? catalogRolesQuery.isLoading : resourcesQuery.isLoading) ? (
            <p className="flex items-center text-sm text-muted-foreground"><Loader2 className="mr-2 h-4 w-4 animate-spin" />Loading {useCatalogRolesForResourcePicker ? 'catalog roles' : resourceLabelPluralLower}...</p>
          ) : form.plannerModelId && useCatalogRolesForResourcePicker && catalogRoleOptions.length === 0 ? (
            <p className="text-sm text-muted-foreground">No catalog roles found for this service instance. Add roles under Organization → Catalog roles.</p>
          ) : form.plannerModelId && !useCatalogRolesForResourcePicker && selectedResources.length === 0 ? (
            <p className="text-sm text-muted-foreground">This planner model has no {resourceLabelPluralLower} yet. Add {resourceLabelPluralLower} in Planner Studio before linking them here.</p>
          ) : linksQuery.isLoading ? (
            <p className="flex items-center text-sm text-muted-foreground"><Loader2 className="mr-2 h-4 w-4 animate-spin" />Loading planner links...</p>
          ) : links.length === 0 ? (
            <p className="text-sm text-muted-foreground">No {resourceLabelPluralLower} are linked to this record yet. Choose a model and {resourceLabelLower} above to add the first link.</p>
          ) : (
            links.map((link) => (
              <div key={String(link.id)} className="rounded-xl border border-border/70 p-3">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-medium text-foreground">{getPlannerLinkResourceLabel(link)}</p>
                      <Badge variant="outline">{getPlannerLinkModelLabel(link)}</Badge>
                      <Badge variant={link.is_required === false ? 'secondary' : 'default'}>{link.is_required === false ? 'Optional' : 'Required'}</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {formatResourceKindLabel(link.planner_resource_kind || 'generic')} resource • Qty {link.quantity ?? 1} • Scope {link.scope || 'default'}
                      {link.planner_resource_kind === 'crew' && (link.default_crew_name || link.default_crew_id)
                        ? ` • Default team: ${link.default_crew_name || `#${link.default_crew_id}`}`
                        : ''}
                    </p>
                    {link.notes ? <p className="text-sm text-muted-foreground">{link.notes}</p> : null}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button type="button" variant="ghost" size="icon" onClick={() => startEdit(link)} aria-label="Edit planner link">
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button type="button" variant="ghost" size="icon" className="text-destructive hover:bg-destructive/10 hover:text-destructive" onClick={() => deleteMutation.mutate(link.id)} aria-label="Delete planner link" disabled={deleteMutation.isPending}>
                      {deleteMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  )
}

export default PlannerLinkManager